import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router";
import Layout from "@/components/layout";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { SettingsSearch } from "@/components/settings/SettingsSearch";
import { useHasPermission } from "@/store/authStore";
import { Permission } from "@forgepoint/shared";
import { useFeatureFlag } from "@/store/featureFlagStore";
import { PersonalSettingsPanel } from "@/components/settings/PersonalSettingsPanel";
import { ERPIntegrationSettingsPanel } from "@/components/settings/ERPIntegrationSettingsPanel";
import { AIWorkersSettingsPanel } from "@/components/settings/AIWorkersSettingsPanel";
import { EmailIntegrationsSettings } from "@/components/settings/EmailIntegrationsSettings";
import { OrganizationSettingsPanel } from "@/components/settings/OrganizationSettingsPanel";
import { QuoteSettingsPanel } from "@/components/settings/QuoteSettingsPanel";
import { OpportunitySettingsPanel } from "@/components/settings/OpportunitySettingsPanel";
import { AgentSettingsPanel } from "@/modules/standardAgent/AgentSettingsPanel";
import { CustomSkillsCard } from "@/modules/standardAgent/components/CustomSkillsCard";
import { PhoneNumbersManagement } from "@/components/PhoneNumbersManagement";
import { PriceModelsSettingsPanel } from "@/components/settings/PriceModelsSettingsPanel";
import { RolesSettingsPanel } from "@/components/settings/RolesSettingsPanel";
import { ItemSettingsPanel } from "@/components/settings/ItemSettingsPanel";
import { CompanySettingsPanel } from "@/components/settings/CompanySettingsPanel";
import { StatusReportSettingsPanel } from "@/components/settings/statusReports/StatusReportSettingsPanel";
import { OnboardingSettingsPanel } from "@/components/settings/onboarding/OnboardingSettingsPanel";

/**
 * Settings page shell that renders tab content based on URL query param.
 * Navigation between settings sections is handled by the sidebar.
 * @returns Settings page element
 */
export function Settings() {
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const location = useLocation();

  const hasSettingsAccess = useHasPermission(Permission.SETTINGS_EDIT);
  const canEditReportSettings = useHasPermission(Permission.SETTINGS_EDIT);
  const statusReportsEnabled = useFeatureFlag("status-reports-cutover");
  // The Report Settings surface is org-gated by the status-reports flag and
  // user-gated by settings.edit — both are required to see it.
  const hasReportSettingsAccess = canEditReportSettings && statusReportsEnabled;
  const hasOnboardingSettingsAccess = useHasPermission(
    Permission.SETTINGS_ONBOARDING_EDIT,
  );
  const emailSearchEnabled = useFeatureFlag("email-search");
  const customRolesEnabled = useFeatureFlag("custom-roles");

  useScrollToSettingsAnchor(location.hash, location.key, tabFromUrl);

  return (
    <Layout breadcrumbs={[{ label: "Settings", current: true }]}>
      <div className="space-y-6 p-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Settings</h2>
          <p className="text-muted-foreground">
            Manage your application settings and integrations.
          </p>
        </div>

        <SettingsSearch />

        <Tabs value={tabFromUrl || "personal"}>
          <TabsContent value="personal" className="space-y-4">
            <PersonalSettingsPanel />
          </TabsContent>

          <TabsContent value="erp" className="space-y-4">
            <ERPIntegrationSettingsPanel />
          </TabsContent>

          {hasSettingsAccess && (
            <TabsContent value="ai-workers" className="space-y-4">
              <AIWorkersSettingsPanel />
            </TabsContent>
          )}

          {emailSearchEnabled && (
            <TabsContent value="email-integrations" className="space-y-4">
              <EmailIntegrationsSettings />
            </TabsContent>
          )}

          <TabsContent value="organization" className="space-y-4">
            <OrganizationSettingsPanel isAdmin={hasSettingsAccess} />
          </TabsContent>

          {hasSettingsAccess && customRolesEnabled && (
            <TabsContent value="roles" className="space-y-4">
              <RolesSettingsPanel />
            </TabsContent>
          )}

          {hasReportSettingsAccess && (
            <TabsContent value="report-settings" className="space-y-4">
              <StatusReportSettingsPanel />
            </TabsContent>
          )}

          <TabsContent value="agent-settings" className="space-y-4">
            <AgentSettingsPanel />
          </TabsContent>

          <TabsContent value="custom-skills" className="space-y-4">
            <CustomSkillsCard />
          </TabsContent>

          <TabsContent value="phone-numbers" className="space-y-4">
            <PhoneNumbersManagement isAdmin={hasSettingsAccess} />
          </TabsContent>

          <TabsContent value="price-models" className="space-y-4">
            <PriceModelsSettingsPanel isAdmin={hasSettingsAccess} />
          </TabsContent>

          <TabsContent value="item-defaults" className="space-y-4">
            <ItemSettingsPanel isAdmin={hasSettingsAccess} />
          </TabsContent>

          <TabsContent value="company-settings" className="space-y-4">
            <CompanySettingsPanel isAdmin={hasSettingsAccess} />
          </TabsContent>

          <TabsContent value="quote-settings" className="space-y-4">
            <QuoteSettingsPanel isAdmin={hasSettingsAccess} />
          </TabsContent>

          <TabsContent value="opportunity-settings" className="space-y-4">
            <OpportunitySettingsPanel isAdmin={hasSettingsAccess} />
          </TabsContent>

          {hasOnboardingSettingsAccess && (
            <TabsContent value="onboarding" className="space-y-4">
              <OnboardingSettingsPanel />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
}

/** Max attempts to find a freshly-mounted tab's anchor element before giving up. */
/** How long to wait for a target field to mount before giving up (ms). */
const SCROLL_ANCHOR_WAIT_MS = 6000;
/** How long the highlight stays on the target field (ms). */
const SCROLL_ANCHOR_HIGHLIGHT_MS = 2000;

/**
 * Scrolls to and briefly highlights the settings field named by the URL hash
 * (set by {@link SettingsSearch}). A target field may not be in the DOM for a
 * while after navigation — its tab has to become active and the panel often
 * loads data asynchronously — so this watches for the anchor to appear via a
 * MutationObserver rather than polling for a fixed window.
 * @param hash - The current URL hash (e.g. "#organization-name")
 * @param locationKey - React Router location key, so re-selecting the same field re-runs the scroll
 * @param tab - The active settings tab, so the effect re-runs after a tab switch
 */
function useScrollToSettingsAnchor(
  hash: string,
  locationKey: string,
  tab: string | null,
) {
  useEffect(() => {
    const anchorId = hash.replace(/^#/, "");
    if (!anchorId) {
      return;
    }

    let done = false;
    let highlightTimeoutId: number | undefined;
    let waitTimeoutId: number | undefined;
    const observer = new MutationObserver(() => attempt());

    function attempt() {
      if (done) {
        return;
      }
      const element = document.getElementById(anchorId);
      if (!element) {
        return;
      }
      done = true;
      observer.disconnect();
      if (waitTimeoutId !== undefined) {
        window.clearTimeout(waitTimeoutId);
      }
      // Defer a frame so the newly-mounted panel has laid out before scrolling.
      requestAnimationFrame(() => {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.classList.add("field-flash-highlight");
        highlightTimeoutId = window.setTimeout(
          () => element.classList.remove("field-flash-highlight"),
          SCROLL_ANCHOR_HIGHLIGHT_MS,
        );
      });
    }

    attempt();
    if (!done) {
      observer.observe(document.body, { childList: true, subtree: true });
      waitTimeoutId = window.setTimeout(() => {
        done = true;
        observer.disconnect();
      }, SCROLL_ANCHOR_WAIT_MS);
    }

    return () => {
      done = true;
      observer.disconnect();
      if (highlightTimeoutId !== undefined) {
        window.clearTimeout(highlightTimeoutId);
      }
      if (waitTimeoutId !== undefined) {
        window.clearTimeout(waitTimeoutId);
      }
    };
  }, [hash, locationKey, tab]);
}
